import * as angular from 'angular';
import { appData } from '../constants/app-data';
import { Subject } from 'rxjs';
import { FineUploaderService } from 'go-modules/fine-uploader/fine-uploader.service';
import { AppEnvService } from 'go-modules/go-app-env/app-env.service';
import { PubnubService } from 'go-modules/go-pubnub/pubnub.service';
import { AnalyticsService } from 'go-modules/services/analytics/analytics.service';
import { FullstoryService } from 'go-modules/services/fullstory/fullstory.service';
import { AppSessionManagerService } from './app-session-manager.service';
import { BannerNotificationsService } from 'go-modules/services/banner-notifications/banner-notifications.service';
import { WatsonUsageTrackingService } from 'go-modules/services/watson-usage-tracking/watson-usage-tracking.service';
import { UserService } from 'go-modules/models/user/user.service';
import { clientSettings } from 'go-modules/models/common/client.settings';
import { Roles } from '@go-modules/enums';
import { GoLocalizeHelperService, Language } from 'go-modules/translation-helper/go-localize-helper.service';
import { SelectedService } from 'go-modules/services/selected/selected.service';
import { upgradeNg1Dependency } from '@go-modules/common/ng1-upgrade-factory';
import { AuthService } from 'go-modules/services/auth/auth.service';
import { LogoutSubscribeOptions } from '@go-modules/services/auth/interfaces/logout-subscribe-options.interface';
import { SessionStorageService } from 'go-modules/session-manager/session-storage.service';
import { GroupsIndexService } from 'go-modules/groups-index/groups-index.service';

export interface InitAppDataOptions {
	minRole?: Roles;
}
export interface AppDataConfig {
	disjoint_groups;
	fine_uploader: any;
	google_analytics: any;
	language: string;
	my_account: any;
	my_orgs: any[];
	partners: any[];
	pubnub: any;
	user: any;
	user_pubnub_channel: string;
	site_notification?: any;
	appVersion: string;
	cacheBustSuffix: any;
}

export class AppDataService {
	public static readonly NG1_INJECTION_NAME = 'AppData' as const;
	public dataConfig;
	public disjoint_groups = [];
	public fine_uploader: any = {};
	public google_analytics: any = {};
	public language: Language = Language.EN;
	public my_account: any = false;
	public my_orgs: any = [];
	public partners: any = [];
	public pubnub: any = {};
	public user: any = {};
	public user_pubnub_channel: string = '';
	public site_notification: any = {};
	public appDataSubject = new Subject();
	public pubnubService: PubnubService;
	public appVersion: string = '';
	public cacheBustSuffix: any = {};

	/* @ngInject */
	constructor (
		private $http: angular.IHttpService,
		private $log: angular.ILogService,
		private $window: angular.IWindowService,
		private $q: ng.IQService,
		public appEnv: AppEnvService,
		pubnub: PubnubService,
		public goLocalizeHelper: GoLocalizeHelperService,
		public fullstoryService: FullstoryService,
		public fineUploader: FineUploaderService,
		public appSessionManagerService: AppSessionManagerService,
		public analyticsService: AnalyticsService,
		public watsonUsageTrackingService: WatsonUsageTrackingService,
		public User,
		private bannerNotificationsService: BannerNotificationsService,
		private userService: UserService,
		public messageModal,
		private selectedService: SelectedService,
		private Group,
		private authService: AuthService,
		private $injector: ng.auto.IInjectorService,
		public sessionStorage: SessionStorageService,
		public groupsIndex: GroupsIndexService
	) {
		this.pubnubService = pubnub;
	}

	/**
	 * Initialize user.
	 * This setup user to app our AppData.
	 */
	public initAppData () {
		if (this.dataConfig) return this.$q.resolve();
		return this.getAppData()
			.then(( { data }: any): void => {
				data.my_orgs = data.my_orgs?.map((item) => {
					item.my_account = this.Group.model(data.my_account);
					item.org_id = item.group_id;
					return item;
				});

				this.selectedService.setMyOrgs(data.my_orgs);
				this.selectedService.setGroups(this.Group.setModels(data.disjoint_groups ?? []));
				if (data.my_account) {
					this.selectedService.setAccount(this.Group.model(data.my_account));
				}
				appData.setData(data);
				this.userService.setCurrentUser(this.User.model(data.user));
				this.setupAppData(data);
				this.dataConfig = data;
				this.setupDependencies();
			})
			.catch((error) => {
				if (error.status === 401) {
					// If we have a 401 the user isn't logged in.
					// Clear all session information and let the routing take them to the login page
					return this.authService.logout().then(() => this.$q.reject());
				}

				// Otherwise we don't know why we are exploding. Tell the user then reload the page
				this.$log.error(error.data);
				this.messageModal.open({
					modalData: {
						title: 'modal-confirm_app_data_error',
						message: 'modal-confirm_app_data_error_message',
						resolveBtnText : 'header_logout',
						rejectBtnText : 'modal-confirm_app_data_error_confirm_btn',
						resolveSecondaryBtnText : 'header_logout'
					}
				})
					.result
					.then(() => {
						// Primary button (logout) was clicked
						return this.authService.logout().then(() => {
							this.$window.location.replace(`https://${this.$window.location.host}/dashboard/auth/login/`);
						});
					})
					.catch((err) => {
						if (err === 'reject') {
							// Secondary button (refresh) was clicked
							this.$window.location.reload();
						}
					});
			});
	}

	public reset () {
		this.dataConfig = null;
		this.clearAppData();
	}

	private getAppData () {
		return this.$http.get(`${clientSettings.GoReactV2API}/dashboard/data`);
	}

	/**
	 * Setup data and publish for the listeners that AppData was updated.
	 *
	 * @param {AppDataConfig} data
	 */
	private setupAppData (data): void {
		this.disjoint_groups = data.disjoint_groups ?? [];
		this.fine_uploader = data.fine_uploader ?? {};
		this.google_analytics = data.googleAnalytics ?? {};
		this.language = data.language ?? Language.EN;
		this.my_account = data.my_account ?? false;
		this.my_orgs = data.my_orgs ?? [];
		this.partners = data.partners ?? [];
		this.pubnub = data.pubnub ?? {};
		this.user = data.user ?? {};
		this.user_pubnub_channel = data.user_pubnub_channel ?? '';
		this.site_notification = data.site_notification ?? {};
		this.cacheBustSuffix = data.cacheBustSuffix ?? {};
		this.appVersion = data.appVersion ?? '';
		this.watsonUsageTrackingService.savePendingTranscriptionUsage();
		this.appDataSubject.next(null);
	}

	/**
	 * Clear appdata on logout.
	 */
	private clearAppData (): void {
		this.disjoint_groups = [];
		this.fine_uploader = {};
		this.google_analytics = {};
		this.language = Language.EN;
		this.my_account = false;
		this.my_orgs = [];
		this.partners = [];
		this.pubnub = {};
		this.user = {};
		this.user_pubnub_channel = '';
		this.site_notification = {};
		this.cacheBustSuffix = {};
		this.appVersion = '';
		this.appDataSubject.next(null);
	}

	public setupDependencies (): void {
		const user = this.User.model(this.user);
		const appEnvData = {
			partners: this.partners,
			disjoint_groups: this.disjoint_groups,
			user_pubnub_channel: this.user_pubnub_channel,
			userCreatedAt: user.created,
			userId: parseInt(user.user_id, 10)
		};
		const analyticsAppData = {
			analytics: this.google_analytics,
			user: this.user
		};
		this.pubnubService.configure(this.pubnub);
		this.appEnv.configure(appEnvData);
		this.goLocalizeHelper.configure(this.language);
		this.appSessionManagerService.configure({user: this.user}, this.$injector.get('eventService'));
		this.fullstoryService.configure(user);
		this.fineUploader.configure(this.fine_uploader);
		this.analyticsService.configure(analyticsAppData);
		this.bannerNotificationsService.configure();
	}

	public resetUserRelatedServices (options?: LogoutSubscribeOptions): void {
		const body = this.$window.document.body;

		this.pubnubService.reset();
		this.fullstoryService.clearUser();
		this.reset();
		this.groupsIndex.clear();
		this.fineUploader.reset();
		this.appEnv.reset();
		this.analyticsService.reset();
		this.goLocalizeHelper.reset();
		this.bannerNotificationsService.reset();
		this.userService.clear();
		this.selectedService.clear();
		// TODO: We shouldn't even have this thing. Anything stored in it
		// should probably be stored in session storage (real) not fake (but actual localstorage)
		// REACT-19421
		this.sessionStorage.clear();

		if (body?.classList.contains('super')) {
			body.classList.remove('super');
		}

		this.$window.sessionStorage.clear();

		if (options?.accountLockTime) {
			localStorage.setItem('accountLockTime', `${options.accountLockTime}`);
		}
	}

	public hasInstructorRole (atleast = false) {
		return this.disjoint_groups.some((group) => {
			return group.hasInstructorRole(atleast);
		});
	}

	public hasReviewerRole (atleast = false) {
		return this.disjoint_groups.some((group) => {
			return group.hasReviewerRole(atleast);
		});
	}

	public hasPresenterRole (atleast = false) {
		return this.disjoint_groups.some((group) => {
			return group.hasPresenterRole(atleast);
		});
	}
}

export const appDataServiceToken = upgradeNg1Dependency(AppDataService);
